import { Component, Input, OnInit, EventEmitter, Output, AfterContentInit, forwardRef, ContentChild } from '@angular/core';
import { FormControl, ControlValueAccessor, NG_VALUE_ACCESSOR, FormControlDirective, FormControlName } from '@angular/forms';
import { FileUploader, FileItem } from 'ng2-file-upload';

export interface PuiUploadFile {
  name: string;
  type?: string;
  size?: any;
  lastModifiedDate?: any;
  headers?: [{name: string, value: string}];
}

export interface PuiUploadResult {
  file: PuiUploadFile;
  success: boolean;
  response: string;
}

export const PUI_FILE_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => PuiFileComponent),
  multi: true
};

let nextId = 0;

@Component({
  exportAs: 'puiInput',
  selector: 'pui-file',
  providers: [
    PUI_FILE_CONTROL_VALUE_ACCESSOR
  ],
  styleUrls: ['file.component.css'],
  templateUrl: 'file.component.html'
})
export class PuiFileComponent implements OnInit, AfterContentInit, ControlValueAccessor {
  @Input() id = `pui-file-${nextId++}`;
  @Input() name: string;
  @Input() label: string;
  @Input() url: string;
  @Input() multiple = true;
  @Input() showProgress = false;
  @Input() showControlButtons = true;
  @Input() dropZoneClass = '';
  @Input() maxFileSize = 1024 * 1024;
  @Input() control: FormControl = new FormControl();
  @Input() disabled: boolean;
  @Input() cancelButton = 'Cancel';
  @Input() uploadButton = 'Upload';

  @Output() beforeUpload: EventEmitter<{ uploadFile: PuiUploadFile, formData: FormData }> =
    new EventEmitter<{ uploadFile: PuiUploadFile, formData: FormData }>();
  @Output() complete: EventEmitter<PuiUploadResult> = new EventEmitter<PuiUploadResult>();
  @Output() error: EventEmitter<PuiUploadResult> = new EventEmitter<PuiUploadResult>();
  @Output() cancel: EventEmitter<any> = new EventEmitter<any>();

  @ContentChild(FormControlName) controlName: FormControlDirective;
  @ContentChild(FormControlDirective) controlDirective: FormControlDirective;

  /** @internal */
  uploader: FileUploader;
  /** @internal */
  hasDropZoneOver: boolean;
  private onTouchedCallback: () => void = () => { };
  private onChangeCallback: (_: any) => void = () => { };

  ngOnInit() {
    this.uploader = new FileUploader({
      isHTML5: true,
      url: this.url
    });
    this.uploader.onAfterAddingFile = (fileItem: FileItem) => {
      this.onTouchedCallback();
      this.control.markAsDirty();
      this.control.markAsTouched();
      // TODO: should better on places where the queue is changed but have no access to it yet
      this.control.setErrors(null);
    };
    this.uploader.onBuildItemForm = (fileItem: FileItem, formData: FormData) => {
      this.beforeUpload.emit({
        uploadFile: this.convert(fileItem),
        formData: formData
      });
    };
    this.uploader.onSuccessItem = (fileItem) => {
      this.uploader.removeFromQueue(fileItem);
    };
    this.uploader.onCompleteItem = (fileItem, response, status, headers) => {
      this.complete.emit({
        file: this.convert(fileItem),
        success: status === 200,
        response: response
      });
    };
    this.uploader.onCompleteAll = () => {
      this.complete.emit(null);
    };
    this.uploader.onErrorItem = (fileItem, response, status, headers) => {
      this.control.setErrors({
        'file-upload': `Failed to upload "${fileItem.file.name}"`
      });
      this.error.emit({
        file: this.convert(fileItem),
        success: status === 200,
        response: response
      });
    };
  }

  ngAfterContentInit(): void {
    if (this.controlDirective) {
      this.control = this.controlDirective.control;
    } else if (this.controlName) {
      this.control = this.controlName.control;
    }
    if (this.control.disabled) {
      this.disabled = true;
    }
  }

  writeValue(obj: any): void {
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  private convert(fileItem: FileItem): PuiUploadFile {
    return {
      name: fileItem.file.name,
      size: fileItem.file.size,
      headers: fileItem.headers,
      lastModifiedDate: fileItem.file.lastModifiedDate
    };
  }

  getClasses() {
    return {
      'well': true,
      'file-drop-zone': true,
      'file-drop-over': this.hasDropZoneOver,
      [this.dropZoneClass]: true
    };
  }

  fileOver(e: any): void {
    this.hasDropZoneOver = e;
  }

  isExceeded(item: FileItem): boolean {
    return item.file.size > this.maxFileSize;
  }

  onCancel() {
    this.uploader.clearQueue();
    this.cancel.emit();
  }

  onUpload() {
    this.uploader.getNotUploadedItems().forEach(item => {
      if (this.isExceeded(item)) {
        this.uploader.removeFromQueue(item);
      }
    });
    this.uploader.uploadAll();
  }
}
